/**
 * CuidaDF - Tela inicial (Home)
 *
 * Mostra o tempo de espera das unidades de saude proximas, e o
 * botao de acesso a triagem. Pede permissao de localizacao do
 * usuario ao carregar.
 */

import React, { useEffect, useState } from "react";
import { buscarFilasAtuais, obterLocalizacaoUsuario } from "../api";
import "../styles/Home.css";

function Home({ onIniciarTriagem }) {
  const [unidades, setUnidades] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erroLocalizacao, setErroLocalizacao] = useState(false);
  const [erroDetalhes, setErroDetalhes] = useState("");

  useEffect(() => {
    carregarUnidades();
  }, []);

  async function carregarUnidades() {
    setCarregando(true);
    try {
      const localizacao = await obterLocalizacaoUsuario();
      const dados = await buscarFilasAtuais(localizacao.latitude, localizacao.longitude);
      setUnidades(dados.unidades || []);
    } catch (erro) {
      const codigo = erro.code ? `Código ${erro.code}` : "";
      const msg = erro.message || String(erro);
      setErroDetalhes(`${codigo}: ${msg}`);
      setErroLocalizacao(true);
      try {
        const dados = await buscarFilasAtuais(null, null);
        setUnidades(dados.unidades || []);
      } catch (erroSecundario) {
        console.error("Erro ao buscar unidades:", erroSecundario);
      }
    } finally {
      setCarregando(false);
    }
  }

  function classificarEspera(minutos) {
    if (minutos <= 30) return "baixo";
    if (minutos <= 60) return "medio";
    return "alto";
  }

  function formatarTempo(minutos) {
    if (minutos < 60) return `${minutos}min`;
    const horas = Math.floor(minutos / 60);
    const min = minutos % 60;
    return min > 0 ? `${horas}h${min}` : `${horas}h`;
  }

  return (
    <div className="tela-home">
      <div className="home-header">
        <p className="home-saudacao">Olá, cidadão(ã) 👋</p>
        <h2 className="home-titulo">Como posso te ajudar hoje?</h2>
        <div className="home-status-bar">
          <span className="status-pill">
            <span className="status-dot" /> {unidades.length} unidades monitoradas
          </span>
          <span className="status-pill">🕐 Tempo real</span>
        </div>
      </div>

      <div className="home-content">
        {erroLocalizacao && (
          <div className="aviso-localizacao">
            Não conseguimos acessar sua localização. Ative a permissão de
            localização para ver as unidades mais próximas de você.
            {erroDetalhes && (
              <div style={{fontSize: "10px", marginTop: "6px", opacity: 0.7, wordBreak: "break-all"}}>
                {erroDetalhes}
              </div>
            )}
          </div>
        )}

        <p className="section-label">Orientação rápida</p>
        <button className="btn-triagem" onClick={onIniciarTriagem}>
          <div className="btn-triagem-left">
            <h3>Avaliar meus sintomas</h3>
            <p>Resultado em menos de 2 minutos</p>
          </div>
          <span className="btn-triagem-icon">🩺</span>
        </button>

        <p className="section-label">Tempo de espera agora — UPAs</p>

        {carregando ? (
          <p className="carregando-texto">Carregando unidades próximas...</p>
        ) : (
          <>
            <div className="upas-grid">
              {unidades
                .filter(u => u.tipo === "UPA")
                .slice(0, 8)
                .map((unidade, idx) => (
                  <div key={idx} className={`upa-card ${classificarEspera(unidade.tempo_espera_minutos)}`}>
                    <p className="upa-name">{unidade.nome}</p>
                    <p className="upa-wait">{formatarTempo(unidade.tempo_espera_minutos)}</p>
                    <p className="upa-label">
                      {unidade.fonte_tempo === "powerbi_igesdf" ? "Tempo real" : "Estimado"}
                      {unidade.distancia_km != null && ` • ${unidade.distancia_km}km`}
                    </p>
                  </div>
                ))}
            </div>

            {unidades.filter(u => u.tipo === "UBS").length > 0 && (
              <>
                <p className="section-label" style={{marginTop: "16px"}}>UBS próximas</p>
                <div className="upas-grid">
                  {unidades
                    .filter(u => u.tipo === "UBS")
                    .slice(0, 4)
                    .map((unidade, idx) => (
                      <div key={idx} className="upa-card ubs-card">
                        <p className="upa-name">{unidade.nome}</p>
                        <p className="upa-wait" style={{fontSize: "13px", color: "#3B9DD4"}}>Atendimento</p>
                        <p className="upa-label">
                          Demanda espontânea
                          {unidade.distancia_km != null && ` • ${unidade.distancia_km}km`}
                        </p>
                      </div>
                    ))}
                </div>
              </>
            )}
          </>
        )}

        <div className="dica-card">
          <span className="dica-icon">💡</span>
          <p className="dica-text">
            <strong>Dica:</strong> Casos de gripe, dor leve e resfriado podem
            ser atendidos na UBS sem filas longas. Faça a avaliação para
            descobrir o que é melhor para você.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Home;
